import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

export interface OnlineUser {
  user_id: string;
  role: 'admin' | 'organizer' | 'runner';
  first_name: string | null;
  city: string | null;
  lat: number | null;
  lng: number | null;
  page: string;
  online_at: string;
}

/**
 * Approximate city-level coordinates for the most common French metros.
 * Avoids GPS prompts; we only use what the user already typed in their profile.
 */
const CITY_COORDS: Record<string, [number, number]> = {
  paris: [48.8566, 2.3522],
  lyon: [45.764, 4.8357],
  marseille: [43.2965, 5.3698],
  toulouse: [43.6047, 1.4442],
  nice: [43.7102, 7.262],
  nantes: [47.2184, -1.5536],
  strasbourg: [48.5734, 7.7521],
  montpellier: [43.6108, 3.8767],
  bordeaux: [44.8378, -0.5792],
  lille: [50.6292, 3.0573],
  rennes: [48.1173, -1.6778],
  reims: [49.2583, 4.0317],
  'le havre': [49.4944, 0.1079],
  'saint-etienne': [45.4397, 4.3872],
  toulon: [43.1242, 5.928],
  grenoble: [45.1885, 5.7245],
  dijon: [47.322, 5.0415],
  angers: [47.4784, -0.5632],
  brest: [48.3904, -4.4861],
  clermont: [45.7772, 3.0870],
  tours: [47.3941, 0.6848],
  amiens: [49.8941, 2.2958],
  metz: [49.1193, 6.1757],
  besancon: [47.2378, 6.0241],
  perpignan: [42.6886, 2.8945],
  orleans: [47.9029, 1.9093],
  caen: [49.1829, -0.3707],
  mulhouse: [47.7508, 7.3359],
  rouen: [49.4432, 1.0993],
  nancy: [48.6921, 6.1844],
  avignon: [43.9493, 4.8055],
  cannes: [43.5528, 7.0174],
  monaco: [43.7384, 7.4246],
};

function jitter(coord: number) {
  return coord + (Math.random() - 0.5) * 0.05;
}

// ============================================================
// Singleton shared channel — one instance for the whole app
// ============================================================
type Listener = (users: OnlineUser[]) => void;
let sharedChannel: ReturnType<typeof supabase.channel> | null = null;
let sharedSubscribed = false;
let currentUsers: OnlineUser[] = [];
const listeners = new Set<Listener>();
let lastPayload: OnlineUser | null = null;

function recompute() {
  if (!sharedChannel) return;
  const state = sharedChannel.presenceState() as Record<string, OnlineUser[]>;
  const flat = Object.values(state).flat();
  const map = new Map<string, OnlineUser>();
  flat.forEach((u) => {
    if (!u?.user_id) return;
    const prev = map.get(u.user_id);
    if (!prev || new Date(u.online_at) > new Date(prev.online_at)) map.set(u.user_id, u);
  });
  currentUsers = Array.from(map.values());
  listeners.forEach((l) => l(currentUsers));
}

function ensureSharedChannel(presenceKey: string) {
  if (sharedChannel) return sharedChannel;
  sharedChannel = supabase.channel('online-users', {
    config: { presence: { key: presenceKey } },
  });
  sharedChannel
    .on('presence', { event: 'sync' }, recompute)
    .on('presence', { event: 'join' }, recompute)
    .on('presence', { event: 'leave' }, recompute)
    .subscribe((status) => {
      if (status === 'SUBSCRIBED') {
        sharedSubscribed = true;
        recompute();
      }
    });
  return sharedChannel;
}

/**
 * Hook for any component (e.g. admin map) to read the live presence list.
 * Mounts the shared channel as observer if no tracker is active.
 */
export function useOnlineUsers(): OnlineUser[] {
  const [users, setUsers] = useState<OnlineUser[]>(currentUsers);

  useEffect(() => {
    // Ensure channel exists even if no authenticated user is tracking yet
    ensureSharedChannel('observer-' + Math.random().toString(36).slice(2, 10));
    listeners.add(setUsers);
    setUsers(currentUsers);
    return () => {
      listeners.delete(setUsers);
    };
  }, []);

  return users;
}

/**
 * Mounts a Supabase Realtime presence channel for the current authenticated user.
 * Only minimal, non-sensitive data is broadcast (user id, role, approximate city/coords, current page).
 */
export function useOnlinePresence() {
  const { user } = useAuth();
  const location = useLocation();

  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    (async () => {
      // Fetch user info (city + role) once at channel mount
      const [{ data: runner }, { data: roles }] = await Promise.all([
        supabase.from('runner_profiles').select('city, first_name').eq('id', user.id).maybeSingle(),
        supabase.from('user_roles').select('role').eq('user_id', user.id),
      ]);
      if (cancelled) return;

      const rolesArr = (roles ?? []).map((r: any) => r.role);
      const role: OnlineUser['role'] = rolesArr.includes('admin')
        ? 'admin'
        : rolesArr.includes('organizer')
          ? 'organizer'
          : 'runner';

      const cityRaw = (runner?.city ?? '').toString().trim().toLowerCase();
      const cityKey = Object.keys(CITY_COORDS).find((k) => cityRaw.includes(k));
      const coords = cityKey ? CITY_COORDS[cityKey] : null;

      // Use the shared channel (created with this user's id as the presence key
      // if no channel exists yet — otherwise we just track on top of it).
      const ch = ensureSharedChannel(user.id);
      const payload = {
        user_id: user.id,
        role,
        first_name: runner?.first_name ?? null,
        city: runner?.city ?? null,
        lat: coords ? jitter(coords[0]) : null,
        lng: coords ? jitter(coords[1]) : null,
        page: location.pathname,
        online_at: new Date().toISOString(),
      };
      lastPayload = payload;
      const tryTrack = async () => {
        if (sharedSubscribed) {
          await ch.track(payload);
        } else {
          setTimeout(tryTrack, 250);
        }
      };
      tryTrack();
    })();

    return () => {
      cancelled = true;
      // Untrack but keep channel alive for observers
      if (sharedChannel && sharedSubscribed) {
        sharedChannel.untrack().catch(() => {});
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Update page on route changes without recreating the channel
  useEffect(() => {
    if (!user || !sharedChannel || !sharedSubscribed) return;
    // Debounce: avoid spamming a Realtime message on every micro-navigation.
    const timer = setTimeout(() => {
      // Presence track() replaces state — always resend the full payload so
      // role / first_name / city / lat / lng survive route changes.
      const base = lastPayload ?? {
        user_id: user.id,
        role: 'runner' as const,
        first_name: null,
        city: null,
        lat: null,
        lng: null,
        page: location.pathname,
        online_at: new Date().toISOString(),
      };
      const next = { ...base, page: location.pathname, online_at: new Date().toISOString() };
      lastPayload = next;
      sharedChannel?.track(next).catch(() => {});
    }, 1500);
    return () => clearTimeout(timer);
  }, [location.pathname, user]);
}